import { type AnnotationMark, type ChartAnnotation, type Paints } from "@beacon/core/charts"
import { createElement, type ReactNode } from "react"

/*
 * The web app's side of a daily chart: each paint as its token, which follows the colour scheme
 * without the chart being rebuilt. The tooltip's chrome is mapped onto the tokens in `tokens.css`,
 * under `.daily-chart`.
 */

/**
 * Each paint as its token's own custom property, which `:root` and `.dark` both declare. Not the
 * `--color-` one Tailwind surfaces: `@theme inline` writes that only where a class names it.
 */
export const screenPaints: Paints = (paint) => `var(--${paint})`

/**
 * What a mark's button is called and its popover tells, where the page doesn't say: one
 * annotation's own name and detail, or, for a mark standing for several, every name and each
 * detail in turn.
 */
export function tellMarkOf({
  annotations,
}: AnnotationMark<ChartAnnotation & { about: string; detail: ReactNode }>) {
  const [only] = annotations
  if (only && annotations.length === 1) return { about: only.about, detail: only.detail }
  return {
    about: annotations.map(({ about }) => about).join(", "),
    detail: createElement(
      "div",
      { className: "space-y-2" },
      ...annotations.map(({ detail }) => detail),
    ),
  }
}

/**
 * Where a day's tooltip hangs from, in the chart's own units, from the day's points: on the desk,
 * at the day's centre over the top of its stack or its highest line (the plot's foot on a day of
 * nothing), the tooltip standing over it; on `compact`, from the plot's top, the tooltip hanging
 * under it. On the desk it stands over every other day's top in `tops` within `reach` px of its
 * centre too (half the tooltip's width), so it never covers a taller neighbour's bars. Never higher
 * than `clearFrom`, the highest it can stand with the tooltip clear of the page's sticky chrome
 * when the chart is scrolled under it, nor below the plot.
 */
export function tooltipAnchorOf({
  points,
  plot,
  clearFrom,
  compact = false,
  tops = [],
  reach = 0,
}: {
  points: readonly { markId: string; x: number; y: number }[]
  plot: { y: number; height: number }
  clearFrom: number
  compact?: boolean
  tops?: readonly { x: number; y: number }[]
  reach?: number
}) {
  const xs = points.map(({ x }) => x)
  const x = (Math.min(...xs) + Math.max(...xs)) / 2
  const foot = plot.y + plot.height
  // Every day has an invisible stop (`days`) the full plot tall; what's drawn is the rest.
  const drawn = points.filter(({ markId }) => markId !== "days").map(({ y }) => y)
  const covered = tops.filter((top) => Math.abs(top.x - x) <= reach).map(({ y }) => y)
  const natural = compact ? plot.y : Math.min(foot, ...drawn, ...covered)
  return { x, y: Math.min(foot, Math.max(natural, clearFrom)) }
}
